import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { B2BProformaService } from '../../../services/B2BServices';
import { getIcon } from '../../../utils/IconHandler';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { formatMoney } from '../../../utils/currency';

const FranchiseProformaRequestModal = ({ open, onClose, onSave, destinationLocationId, destinationLocationName }) => {
  const { t } = useTranslation();
  const [originBranchId, setOriginBranchId] = useState(null);
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !destinationLocationId) {
      setOriginBranchId(null);
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await B2BProformaService.getFranchiseRequestProducts(destinationLocationId);
        setOriginBranchId(response?.branchId || null);
        setProducts(response?.products || []);
      } catch (error) {
        console.error('Error fetching B2B franchise products:', error);
        setOriginBranchId(null);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [open, destinationLocationId]);

  const handleClose = () => {
    setItems([]);
    setSelectedProduct(null);
    setQuantity('');
    setNotes('');
    setOriginBranchId(null);
    setProducts([]);
    setSaving(false);
    onClose();
  };

  const handleAddItem = () => {
    if (!selectedProduct) {
      SnackbarUtils.error(t('product_required'));
      return;
    }

    const parsedQuantity = Number(quantity);
    if (!parsedQuantity || parsedQuantity <= 0) {
      SnackbarUtils.error(t('quantity_required'));
      return;
    }

    setItems(prev => {
      const existingIndex = prev.findIndex(item => item.productId === selectedProduct.id);
      if (existingIndex >= 0) {
        return prev.map((item, index) => (
          index === existingIndex
            ? { ...item, quantity: item.quantity + parsedQuantity }
            : item
        ));
      }
      return [
        ...prev,
        {
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          quantity: parsedQuantity,
          unitPrice: selectedProduct.unitPrice || 0,
          finalUnitPrice: selectedProduct.finalUnitPrice || selectedProduct.unitPrice || 0,
          discountType: selectedProduct.discountType,
          discountValue: selectedProduct.discountValue || 0
        }
      ];
    });
    setSelectedProduct(null);
    setQuantity('');
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSave = async () => {
    if (!destinationLocationId) {
      SnackbarUtils.error(t('branch_required'));
      return;
    }
    if (!originBranchId) {
      SnackbarUtils.error(t('branch_required'));
      return;
    }
    if (items.length === 0) {
      SnackbarUtils.error(t('items_required'));
      return;
    }

    try {
      setSaving(true);
      await onSave({
        destinationLocationId: Number(destinationLocationId),
        branchId: Number(originBranchId),
        notes: notes.trim() || null,
        items: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        }))
      });
      SnackbarUtils.success(t('proforma_created_successfully'));
      handleClose();
    } catch (error) {
      console.error('Error creating franchise B2B request:', error);
      const backendMessage = error?.response?.data?.error_message || error?.response?.data?.message;
      SnackbarUtils.error(backendMessage || t('error_creating_proforma'));
    } finally {
      setSaving(false);
    }
  };

  const productOptions = useMemo(() => (
    products.filter(product => product?.id && product?.name)
  ), [products]);

  const formatCurrency = (amount) => (
    formatMoney(Number(amount) || 0)
  );

  const getDiscountLabel = (item) => {
    if (!item.discountValue) return '-';
    if (item.discountType === 'PERCENTAGE') return `${item.discountValue}%`;
    return formatCurrency(item.discountValue);
  };

  const totalAmount = useMemo(() => (
    items.reduce((total, item) => total + ((Number(item.finalUnitPrice) || 0) * item.quantity), 0)
  ), [items]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Nuevo pedido B2B</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {destinationLocationName ? `Solicitud para ${destinationLocationName}` : 'Solicitud para franquicia'}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 140px 140px' }, gap: 2, alignItems: 'stretch' }}>
            <Autocomplete
              fullWidth
              options={productOptions}
              getOptionLabel={(option) => option.name || ''}
              value={selectedProduct}
              onChange={(_, value) => setSelectedProduct(value)}
              disabled={loadingProducts || !originBranchId}
              loading={loadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('product')}
                  placeholder={t('search_product')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
            <TextField
              label={t('quantity')}
              type="number"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              inputProps={{ min: 1 }}
            />
            <Button
              variant="outlined"
              onClick={handleAddItem}
              disabled={loadingProducts || !originBranchId}
              sx={{ minHeight: 56, height: '100%', alignSelf: 'stretch' }}
            >
              {t('add')}
            </Button>
          </Box>

          {items.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('product')}</TableCell>
                    <TableCell align="center">{t('quantity')}</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Descuento</TableCell>
                    <TableCell align="right">Precio final</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={`${item.productId}-${index}`}>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell align="right">{getDiscountLabel(item)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.finalUnitPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.finalUnitPrice * item.quantity)}</TableCell>
                      <TableCell align="right">
                        <IconButton color="error" size="small" onClick={() => handleRemoveItem(index)}>
                          {getIcon('trash')}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Total estimado: {formatCurrency(totalAmount)}
                </Typography>
              </Box>
            </TableContainer>
          )}

          <TextField
            label={t('notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t('cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || items.length === 0}>
          {saving ? <CircularProgress size={20} /> : 'Enviar solicitud'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FranchiseProformaRequestModal;
